import get from 'lodash/get'
import flow from 'lodash/fp/flow'
import sortBy from 'lodash/fp/sortBy'
import defaults from 'lodash/defaults'
import clone from 'lodash/clone'
// template strings
const itemOptionLabelTemplate = (
  process.env.ITEM_OPTIONS_LABEL_TEMPLATE ||
  `Option :optionIndex`
)
const itemOptionTemplate = (
  process.env.ITEM_OPTIONS_TEMPLATE ||
  `(:quantity) :briefDescription - sku: :partId - :engraving`
)
/**
 * @param {string} tpl
 * @param {object} vars
 * @param {number} [vars.optionIndex]
 * @param {number} vars.quantity
 * @param {string} vars.partId
 * @param {string} vars.briefDescription
 * @return {string}
 */
const renderTemplate = (tpl, {
  optionIndex,
  quantity,
  partId,
  briefDescription,
  engraving
}) => {
  return tpl
    .replace(':optionIndex', `${optionIndex || 'N/A'}`)
    .replace(':quantity', `${quantity}`)
    .replace(':partId', `${partId}`)
    .replace(':briefDescription', `${briefDescription}`)
    .replace(':engraving', `${engraving}`)
}
/**
 * @param {object} options
 * @param {object} options.item
 * @param {object} options.item.variant
 * @param {number} [options.item.quantity=1]
 * @param {string} options.item.variant.title
 * @param {string} options.item.variant.sku
 * @param {object} options.item.variant.metafieldsMap
 * @param {{value: string}} options.item.variant.metafieldsMap.raw_sku
 * @param {{value: string}} options.item.variant.metafieldsMap.brief_description
 * @param {number} [options.optionIndex]
 */
export const getItemFields = ({ item, optionIndex }) => {
  const title = get(item, 'variant.title') || get(item, 'title')
  const quantity = get(item, 'quantity') || 1
  const sku = get(item, 'variant.sku', '')
  let engraving = ''
  if (item.engravingState) {
    engraving = 'custom:'
    for (let { key, value } of get(item, 'engravingState.props', [])) {
      engraving += `\n${key}: ${value}`
    }
  }
  // engravingState
  let partId
  if (sku.startsWith('RR-')) {
    partId = sku
  } else {
    partId = get(
      item,
      'variant.metafieldsMap.raw_sku.value',
      (process.env.NODE_ENV !== 'production' && quantity) ? false : undefined
    )
  }
  const fallbackDescription = `MISSING BRIEF_DESCRIPTION (title: ${title || '?'} / partId: ${partId || '?'} / sku: ${sku || '?'})`
  const briefDescription = (
    get(item, 'variant.metafieldsMap.brief_description.value') ||
    get(item, 'metafieldsMap.brief_description.value') ||
    fallbackDescription
  )
  return {
    title,
    quantity,
    partId,
    briefDescription,
    optionIndex,
    engraving,
  }
}
/**
 * @param {object} productOrVariant
 * @return {string}
 */
export const getBriefDescription = (productOrVariant) => getItemFields({ item: productOrVariant }).briefDescription
/**
 * @param {object} options
 * @param {string} options.title
 * @param {number} options.quantity
 * @param {string} options.partId
 * @param {string} options.briefDescription
 * @param {number} [options.optionIndex]
 * @return {{uiOption: object, configItem: object, packingSlipItemOption: string}}
 */
export const getItemOptions = ({
  title,
  quantity,
  partId,
  briefDescription,
  optionIndex,
  engraving,
}) => {
  const vars = {
    optionIndex,
    quantity,
    partId,
    briefDescription,
    engraving,
  }
  return {
    uiOption: {
      key: `Option ${optionIndex}`,
      value: title
    },
    configItem: {
      quantity,
      part_id: partId,
    },
    packingSlipItemOption: renderTemplate(itemOptionTemplate, vars),
    renderItemOption: (fallbackVars) =>
      renderTemplate(itemOptionTemplate, defaults(clone(vars), fallbackVars))
  }
}
/**
 * @param {object} options
 * @param {object} options.item
 * @param {object} options.item.variant
 * @param {number} [options.item.quantity=1]
 * @param {string} options.item.variant.title
 * @param {string} options.item.variant.sku
 * @param {object} options.item.variant.metafieldsMap
 * @param {{value: string}} options.item.variant.metafieldsMap.raw_sku
 * @param {{value: string}} options.item.variant.metafieldsMap.brief_description
 * @param {number} [options.optionIndex]
 * @return {{uiOption: object, configItem: object, packingSlipItemOption: string}}
 */
export const getBundleMetafields = flow(getItemFields, getItemOptions)
/**
 * @param {object} options
 * @param {string[]} options.packingSlipItemOptions
 * @link https://www.shipstation.com/docs/api/orders/create-update-order/
 * ```
 * {...createUpdateOrderRequest, options: [
 *   {
 *     "name": "Size",
 *     "value": "Large"
 *   }
 * ]}
 * ```
 */
export const formatPackingSlipProp = flow(
  ({ packingSlipItemOptions: options }) => options,
  sortBy((value) => {
    const skuMatch = value.match(/sku:[ ]?([A-Z]+-[A-Za-z0-9]+)/)
    if (skuMatch && skuMatch.length) {
      return skuMatch[1]
    } else {
      return value.toUpperCase()
    }
  }),
  (options) => options.map((value, i) => {
    return {
      name: renderTemplate(itemOptionLabelTemplate, { optionIndex: i + 1 }),
      value
    }
  }),
  (value) => ({ key: '_packing_slip_item_options', value })
)
